import React, { useMemo } from "react";
import { Box, useMediaQuery } from "@mui/material";
import {
  HourglassTopRounded as AwaitingIcon,
  BuildCircleOutlined as InProgressIcon,
  AssignmentTurnedInOutlined as NeedsConfirmIcon,
  CheckCircleOutline as CompletedIcon,
  PublicOutlined as OpenIcon,
  ViewListRounded as AllIcon,
} from "@mui/icons-material";
import { ServiceRequestStatusCode, ServiceRequestsFilters } from "../models/models";
import { dt } from "../../../shared/themes/redesignTokens";

// ════════════════════════════════════════════════════════════════
//  Strip badge config — owner's perspective
// ════════════════════════════════════════════════════════════════

interface StripBadge {
  /** The status code(s) this badge represents. Array for grouped statuses like "Open". */
  statusCodes: ServiceRequestStatusCode[];
  /** Unique key for the badge (used for active filter matching). */
  key: string;
  labelKey: string;
  icon: React.ReactElement;
  accent: string;
  bg: string;
  glow: string;
  pulse?: boolean;
}

const STRIP_BADGES: StripBadge[] = [
  {
    key: "open",
    statusCodes: [ServiceRequestStatusCode.openForOffers, ServiceRequestStatusCode.negotiating],
    labelKey: "servicing:actionStrip.open",
    icon: <OpenIcon sx={{ fontSize: "15px" }} />,
    accent: "#6B7280",
    bg: "#F4F6F9",
    glow: "rgba(107,114,128,0.10)",
  },
  {
    key: "awaitingProvider",
    statusCodes: [ServiceRequestStatusCode.awaitingProviderConfirmation],
    labelKey: "servicing:actionStrip.awaitingProvider",
    icon: <AwaitingIcon sx={{ fontSize: "15px" }} />,
    accent: "#F59E0B",
    bg: "#FFFBEB",
    glow: "rgba(245,158,11,0.10)",
    pulse: true,
  },
  {
    key: "inProgress",
    statusCodes: [ServiceRequestStatusCode.inProgress],
    labelKey: "servicing:actionStrip.inProgress",
    icon: <InProgressIcon sx={{ fontSize: "15px" }} />,
    accent: "#3B82F6",
    bg: "#EFF6FF",
    glow: "rgba(59,130,246,0.10)",
  },
  {
    key: "needsConfirmation",
    statusCodes: [ServiceRequestStatusCode.awaitingCompletionConfirmation],
    labelKey: "servicing:actionStrip.needsConfirmation",
    icon: <NeedsConfirmIcon sx={{ fontSize: "15px" }} />,
    accent: "#E67E22",
    bg: "#FDF2E9",
    glow: "rgba(230,126,34,0.10)",
    pulse: true,
  },
  {
    key: "completed",
    statusCodes: [ServiceRequestStatusCode.completed],
    labelKey: "servicing:actionStrip.completed",
    icon: <CompletedIcon sx={{ fontSize: "15px" }} />,
    accent: "#27AE60",
    bg: "#EAFAF1",
    glow: "rgba(39,174,96,0.10)",
  },
];

// ════════════════════════════════════════════════════════════════
//  Props
// ════════════════════════════════════════════════════════════════

interface ServiceRequestsActionStripProps {
  filters?: ServiceRequestsFilters;
  /** The currently active badge key (null = "All" / no filter). */
  activeFilterKey?: string | null;
  translate: (key: string, options?: any) => any;
  /** Called when a status badge is clicked. Receives the badge key. */
  onBadgeClick: (badgeKey: string) => void;
  /** Called when "All" badge is clicked. */
  onAllClick: () => void;
}

// ════════════════════════════════════════════════════════════════
//  Component
// ════════════════════════════════════════════════════════════════

const ServiceRequestsActionStrip: React.FC<ServiceRequestsActionStripProps> = ({
  filters,
  activeFilterKey,
  translate,
  onBadgeClick,
  onAllClick,
}) => {
  const isPhone = useMediaQuery("(max-width:768px)");

  // Compute counts: sum filter values for all statusCodes in each badge
  const badgeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    STRIP_BADGES.forEach((badge) => {
      let total = 0;
      badge.statusCodes.forEach((code) => {
        const filter = filters?.statusCodes?.find((f) => f.id === code);
        total += filter?.value ?? 0;
      });
      counts[badge.key] = total;
    });
    return counts;
  }, [filters]);

  // Total actionable items
  const totalActionable = useMemo(
    () => Object.values(badgeCounts).reduce((sum, c) => sum + c, 0),
    [badgeCounts]
  );

  // Hide strip when nothing exists
  if (totalActionable === 0) return null;

  const isAllActive = activeFilterKey == null;

  return (
    <Box
      sx={{
        backgroundColor: dt.cardBg,
        borderRadius: dt.radiusSm,
        border: `1px solid ${dt.cardBorder}`,
        boxShadow: dt.cardShadow,
        padding: isPhone ? "10px 12px" : "12px 20px",
        display: "flex",
        alignItems: "center",
        gap: isPhone ? "8px" : "12px",
        overflowX: "auto",
        overflowY: "hidden",
        minHeight: isPhone ? "52px" : "56px",
        animation: "fadeUp 0.4s cubic-bezier(0.4, 0, 0.2, 1) both",
        "&::-webkit-scrollbar": { display: "none" },
        scrollbarWidth: "none",
      }}
    >
      {/* Strip label (desktop only) */}
      {!isPhone && (
        <Box
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1.2px",
            color: dt.textMuted,
            fontFamily: dt.font,
            whiteSpace: "nowrap",
            flexShrink: 0,
            mr: "4px",
          }}
        >
          {translate("servicing:actionStrip.title")}
        </Box>
      )}

      {/* Divider (desktop only) */}
      {!isPhone && (
        <Box
          sx={{
            width: "1px",
            height: "28px",
            backgroundColor: dt.cardBorder,
            flexShrink: 0,
          }}
        />
      )}

      {/* "All" badge */}
      <Box
        onClick={onAllClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          backgroundColor: isAllActive ? "#F0F2F7" : "#F8F9FA",
          border: `1px solid ${isAllActive ? "#9CA3AF40" : "#E5E7EB"}`,
          borderRadius: dt.radiusPill,
          padding: isPhone ? "5px 10px" : "6px 14px",
          cursor: "pointer",
          whiteSpace: "nowrap",
          flexShrink: 0,
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          ...(isAllActive && {
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          }),
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            borderColor: "#9CA3AF40",
          },
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "20px",
            height: "20px",
            borderRadius: "10px",
            backgroundColor: isAllActive ? "#6B7280" : "#9CA3AF",
            color: "#FFFFFF",
            fontSize: "10px",
            fontWeight: 700,
            fontFamily: dt.fontMono,
            padding: "0 4px",
          }}
        >
          {totalActionable}
        </Box>
        <Box sx={{ display: "flex", color: isAllActive ? "#6B7280" : "#9CA3AF" }}>
          <AllIcon sx={{ fontSize: "15px" }} />
        </Box>
        <Box
          sx={{
            fontSize: isPhone ? "11px" : "12px",
            fontWeight: 600,
            fontFamily: dt.font,
            color: isAllActive ? "#6B7280" : "#9CA3AF",
            letterSpacing: "0.1px",
          }}
        >
          {translate("servicing:actionStrip.all")}
        </Box>
      </Box>

      {/* Status badges */}
      {STRIP_BADGES.map((badge) => {
        const count = badgeCounts[badge.key];
        if (count === 0) return null;

        const isActive = activeFilterKey === badge.key;

        return (
          <Box
            key={badge.key}
            onClick={() => onBadgeClick(badge.key)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: badge.bg,
              border: `1px solid ${isActive ? badge.accent + "60" : badge.accent + "20"}`,
              borderRadius: dt.radiusPill,
              padding: isPhone ? "5px 10px" : "6px 14px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
              ...(isActive && {
                boxShadow: `0 2px 8px ${badge.glow}, inset 0 0 0 1px ${badge.accent}30`,
              }),
              ...(badge.pulse && !isActive && {
                animation: "stripPulse 2.5s ease-in-out infinite",
              }),
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: `0 2px 8px ${badge.glow}`,
                borderColor: `${badge.accent}40`,
              },
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "20px",
                height: "20px",
                borderRadius: "10px",
                backgroundColor: badge.accent,
                color: "#FFFFFF",
                fontSize: "10px",
                fontWeight: 700,
                fontFamily: dt.fontMono,
                padding: "0 4px",
              }}
            >
              {count}
            </Box>
            <Box sx={{ display: "flex", color: badge.accent }}>
              {badge.icon}
            </Box>
            <Box
              sx={{
                fontSize: isPhone ? "11px" : "12px",
                fontWeight: 600,
                fontFamily: dt.font,
                color: badge.accent,
                letterSpacing: "0.1px",
              }}
            >
              {translate(badge.labelKey)}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// Export the badge config so MyServiceRequests can map key -> statusCodes
export const SR_STRIP_BADGE_MAP: Record<string, ServiceRequestStatusCode[]> = {};
STRIP_BADGES.forEach((b) => { SR_STRIP_BADGE_MAP[b.key] = b.statusCodes; });

export default ServiceRequestsActionStrip;
